import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import Button from "@/components/common/Button";
import {
    Upload,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader,
    Sparkles,
    X,
    Check,
} from "lucide-react";
import mammoth from "mammoth";
import { parseDocxXml } from "@/lib/cat/docxXmlParser";
import { parseXliff } from "@/lib/cat/xliffParser";
import { parsePDFViaAPI } from "@/lib/cat/pdfParserViaAPI";
import { checkPdfComplexity } from "@/lib/cat/pdfPreflightCheck";
import {
    extractTerminology,
    SuggestedTerm,
} from "@/lib/cat/terminologyExtractor";
import { toPreAO } from "@/lib/cat/orthoConverter";
import { incrementStorageUsage } from "@/lib/cat/usageService";

const LANGUAGE_OPTIONS = [
    { value: "pt-pt", label: "Portuguese — Portugal" },
    { value: "pt-br", label: "Portuguese — Brazil" },
    { value: "pt-ao", label: "Portuguese — Angola" },
    { value: "pt-mz", label: "Portuguese — Mozambique" },
    { value: "af", label: "Afrikaans" },
    { value: "ar", label: "Arabic" },
    { value: "bg", label: "Bulgarian" },
    { value: "zh", label: "Chinese" },
    { value: "hr", label: "Croatian" },
    { value: "cs", label: "Czech" },
    { value: "da", label: "Danish" },
    { value: "nl", label: "Dutch" },
    { value: "en", label: "English" },
    { value: "et", label: "Estonian" },
    { value: "fi", label: "Finnish" },
    { value: "fr", label: "French" },
    { value: "de", label: "German" },
    { value: "el", label: "Greek" },
    { value: "hu", label: "Hungarian" },
    { value: "id", label: "Indonesian" },
    { value: "it", label: "Italian" },
    { value: "ja", label: "Japanese" },
    { value: "ko", label: "Korean" },
    { value: "lv", label: "Latvian" },
    { value: "lt", label: "Lithuanian" },
    { value: "nb", label: "Norwegian" },
    { value: "pl", label: "Polish" },
    { value: "ro", label: "Romanian" },
    { value: "ru", label: "Russian" },
    { value: "sk", label: "Slovak" },
    { value: "sl", label: "Slovenian" },
    { value: "es", label: "Spanish" },
    { value: "sw", label: "Swahili" },
    { value: "sv", label: "Swedish" },
    { value: "tr", label: "Turkish" },
    { value: "uk", label: "Ukrainian" },
] as const;

const PT_PREAO_TARGETS = new Set(["pt-pt", "pt", "pt-ao", "pt-mz"]);

const SAMPLE_DOCUMENT_TEXT = `Service Agreement

This agreement is entered into between the Client and the Service Provider.

The Service Provider agrees to deliver professional translation services
as described in this agreement.

All translations shall be completed within the agreed deadline.

The Client shall provide source documents in a timely manner.

Payment is due within 30 days of delivery.

This agreement is governed by the laws of South Africa.`;

function createSampleFile(): File {
    const blob = new Blob([SAMPLE_DOCUMENT_TEXT], { type: "text/plain" });
    return new File([blob], "sample-service-agreement.txt", {
        type: "text/plain",
    });
}

async function hasTrackChanges(file: File): Promise<boolean> {
    try {
        const JSZip = (await import("jszip")).default;
        const zip = await JSZip.loadAsync(file);
        const docXml = zip.files["word/document.xml"];
        if (!docXml) return false;
        const content = await docXml.async("string");
        return /<w:ins[\s>]/.test(content) || /<w:del[\s>]/.test(content);
    } catch {
        return false;
    }
}

interface FileUploadProps {
    onFileSelect: (files: File[]) => void;
    selectedFiles: File[];
    error?: string;
}

const SUPPORTED_FORMATS = [
    ".txt",
    ".docx",
    ".pdf",
    ".csv",
    ".po",
    ".xlf",
    ".xliff",
    ".xml",
];

const FileUpload: React.FC<FileUploadProps> = ({
    onFileSelect,
    selectedFiles,
    error,
}) => {
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover")
            setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.length)
            validateAndSelectFiles(Array.from(e.dataTransfer.files));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (e.target.files?.length)
            validateAndSelectFiles(Array.from(e.target.files));
    };

    const validateAndSelectFiles = (files: File[]) => {
        const validFiles: File[] = [];
        let totalSize = 0;

        for (const file of files) {
            const fileExt = "." + file.name.toLowerCase().split(".").pop();
            if (!SUPPORTED_FORMATS.includes(fileExt)) {
                alert(
                    `Unsupported file type: ${fileExt} (${file.name}). Supported: ${SUPPORTED_FORMATS.join(", ")}`,
                );
                continue;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert(`File ${file.name} exceeds 10MB limit`);
                continue;
            }
            if (selectedFiles.length + validFiles.length >= 10) {
                alert("Maximum 10 files allowed");
                break;
            }
            totalSize += file.size;
            if (totalSize > 50 * 1024 * 1024) {
                alert("Total file size exceeds 50MB limit");
                break;
            }
            validFiles.push(file);
        }

        if (validFiles.length > 0) {
            onFileSelect([...selectedFiles, ...validFiles]);
        }
    };

    return (
        <div className="space-y-4">
            <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${dragActive ? "border-gold-600 bg-gold-50" : "border-gray-300 hover:border-gray-400"}
          ${error ? "border-red-300 bg-red-50" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <input
                    type="file"
                    className="hidden"
                    id="file-upload"
                    onChange={handleChange}
                    accept={SUPPORTED_FORMATS.join(",")}
                    multiple
                />
                <Upload
                    className={`mx-auto h-12 w-12 mb-4 ${dragActive ? "text-gold-600" : "text-gray-600"}`}
                />
                {selectedFiles.length > 0 ? (
                    <div className="space-y-2">
                        <div className="flex items-center justify-center space-x-2 text-green-600">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-medium">
                                {selectedFiles.length} file
                                {selectedFiles.length !== 1 ? "s" : ""} selected
                            </span>
                        </div>
                        <p className="text-sm text-gray-700">
                            Total size:{" "}
                            {(
                                selectedFiles.reduce(
                                    (sum, file) => sum + file.size,
                                    0,
                                ) /
                                1024 /
                                1024
                            ).toFixed(2)}{" "}
                            MB
                        </p>
                    </div>
                ) : (
                    <>
                        <p className="text-gray-700 mb-2">
                            Drag and drop files here, or{" "}
                            <label
                                htmlFor="file-upload"
                                className="text-gold-600 hover:text-gold-700 cursor-pointer font-medium"
                            >
                                browse
                            </label>
                        </p>
                        <p className="text-sm text-gray-700">
                            Supported formats: TXT, DOCX, PDF, CSV, PO, XLIFF,
                            XML
                        </p>
                        <p className="text-xs text-gray-600 mt-2">
                            Maximum 10 files, 50MB total
                        </p>
                    </>
                )}
            </div>

            {/* File list with remove buttons */}
            {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                    {selectedFiles.map((file, index) => (
                        <div
                            key={`${file.name}-${file.size}-${index}`}
                            className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                        >
                            <div className="flex items-center space-x-3">
                                <FileText className="h-4 w-4 text-gray-500" />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {(file.size / 1024).toFixed(1)} KB
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const newFiles = [...selectedFiles];
                                    newFiles.splice(index, 1);
                                    onFileSelect(newFiles);
                                }}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                aria-label={`Remove ${file.name}`}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}
        </div>
    );
};

// ── Terminology Approval Modal ────────────────────────────────────────────────

interface TermModalProps {
    terms: SuggestedTerm[];
    onToggle: (index: number) => void;
    onConfirm: () => void;
    onClose: () => void;
}

const TermModal: React.FC<TermModalProps> = ({
    terms,
    onToggle,
    onConfirm,
    onClose,
}) => {
    const approvedCount = terms.filter((t) => t.approved).length;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                        <Sparkles className="h-5 w-5 text-gold-600" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            Extracted Terminology
                        </h2>
                        <span className="ml-2 px-2 py-0.5 bg-gold-100 text-gold-800 text-xs font-medium rounded-full">
                            {approvedCount} of {terms.length} selected
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                {/* Subheader */}
                <div className="px-6 py-3 bg-gold-50 border-b border-gold-100">
                    <p className="text-sm text-gold-800">
                        TranslyxAI identified these key terms. Tick the ones to
                        add to your project glossary — all are pre-selected.
                    </p>
                </div>
                {/* Column headers */}
                <div className="px-6 pt-3">
                    <div className="grid grid-cols-12 gap-3 px-2 mb-1">
                        <div className="col-span-1"></div>
                        <div className="col-span-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Source
                        </div>
                        <div className="col-span-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Translation
                        </div>
                        <div className="col-span-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Definition
                        </div>
                        <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Domain
                        </div>
                    </div>
                </div>
                {/* Terms list */}
                <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-2">
                    {terms.map((term, index) => (
                        <div
                            key={index}
                            onClick={() => onToggle(index)}
                            className={`grid grid-cols-12 gap-3 items-start p-3 rounded-lg border cursor-pointer transition-all
                ${
                    term.approved
                        ? "border-gold-300 bg-gold-50 hover:bg-gold-100"
                        : "border-gray-200 bg-gray-50 hover:bg-gray-100 opacity-60"
                }`}
                        >
                            <div className="col-span-1 flex items-center justify-center pt-0.5">
                                <div
                                    className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors flex-shrink-0
                  ${term.approved ? "bg-gold-600 border-gold-600" : "border-gray-300 bg-white"}`}
                                >
                                    {term.approved && (
                                        <Check className="h-3 w-3 text-white" />
                                    )}
                                </div>
                            </div>
                            <div className="col-span-3">
                                <span className="text-sm font-medium text-gray-900">
                                    {term.term_source}
                                </span>
                            </div>
                            <div className="col-span-3">
                                <span className="text-sm text-gray-700">
                                    {term.term_target}
                                </span>
                            </div>
                            <div className="col-span-4">
                                <span className="text-xs text-gray-500 leading-relaxed">
                                    {term.definition || "—"}
                                </span>
                            </div>
                            <div className="col-span-1">
                                <span className="inline-block px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full truncate max-w-full">
                                    {term.domain}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        Skip — don't add any terms
                    </button>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() =>
                                terms.forEach((t, i) => {
                                    if (!t.approved) onToggle(i);
                                })
                            }
                            className="text-sm text-gold-700 hover:text-gold-900 font-medium transition-colors"
                        >
                            Select all
                        </button>
                        <Button
                            variant="primary"
                            onClick={onConfirm}
                            disabled={approvedCount === 0}
                        >
                            Add {approvedCount} term
                            {approvedCount !== 1 ? "s" : ""} to glossary
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const NewProjectPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Wizard state
    const [currentStep, setCurrentStep] = useState(1);
    const [mtProvider, setMtProvider] = useState<
        "deepl" | "google" | "mymemory"
    >("deepl");
    const [aiGlossaryExtraction, setAiGlossaryExtraction] = useState(true);

    const [projectName, setProjectName] = useState("");
    const [sourceLanguage, setSourceLanguage] = useState("en");
    const [targetLanguage, setTargetLanguage] = useState("pt-pt");
    const [orthographicStandard, setOrthographicStandard] = useState<
        "post_ao" | "pre_ao"
    >("post_ao");
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [debugInfo, setDebugInfo] = useState<string>("");
    const [pdfComplexityWarning, setPdfComplexityWarning] = useState<
        string | null
    >(null);
    const [pdfWarningDismissed, setPdfWarningDismissed] = useState(false);

    const [parsedResult, setParsedResult] = useState<{
        segments: { source: string; target: string; paragraphIndex?: number }[];
        wordCount: number;
    } | null>(null);

    const [extracting, setExtracting] = useState(false);
    const [extractError, setExtractError] = useState<string | null>(null);
    const [suggestedTerms, setSuggestedTerms] = useState<SuggestedTerm[]>([]);
    const [showTermModal, setShowTermModal] = useState(false);
    const [confirmedTerms, setConfirmedTerms] = useState<SuggestedTerm[]>([]);
    const [termsAdded, setTermsAdded] = useState(false);

    // Step validation
    const canGoToStep2 =
        projectName.trim() !== "" && sourceLanguage && targetLanguage;
    const canGoToStep3 = selectedFiles.length > 0;
    const canCreateProject = canGoToStep2 && canGoToStep3;

    // Reset step when languages change
    useEffect(() => {
        if (!PT_PREAO_TARGETS.has(targetLanguage)) {
            setOrthographicStandard("post_ao");
        }
    }, [targetLanguage]);

    const parseFileContent = async (file: File) => {
        const fileExt = file.name.toLowerCase().split(".").pop();
        let content = "";

        if (fileExt === "docx") {
            setDebugInfo("Parsing DOCX file...");
            try {
                const xmlSegments = await parseDocxXml(file);
                const wordCount = xmlSegments.reduce(
                    (sum, seg) =>
                        sum + seg.source.split(" ").filter(Boolean).length,
                    0,
                );
                return {
                    segments: xmlSegments.map((seg) => ({
                        source: seg.source,
                        target: "",
                        paragraphIndex: seg.paragraphIndex,
                    })),
                    wordCount,
                };
            } catch (err) {
                console.warn("XML parse failed, falling back:", err);
                const arrayBuffer = await file.arrayBuffer();
                const result = await mammoth.extractRawText({ arrayBuffer });
                content = result.value;
            }
        } else if (fileExt === "pdf") {
            // Use server-side PDF parsing API
            try {
                const result = await parsePDFViaAPI(file);
                return {
                    segments: result.segments,
                    wordCount: result.wordCount,
                };
            } catch (error) {
                console.error("PDF parsing failed:", error);
                // Fallback to basic message if API fails
                return {
                    segments: [
                        {
                            source: `PDF parsing failed: ${error instanceof Error ? error.message : "Unknown error"}. Please try again or use TXT/DOCX format.`,
                            target: "",
                        },
                    ],
                    wordCount: 0,
                };
            }
        } else {
            content = await file.text();
        }

        let segments: { source: string; target: string }[] = [];

        if (fileExt === "csv") {
            const lines = content.split("\n").filter((l) => l.trim());
            const longestColumn = Math.max(
                ...lines.map((l) => l.split(",").length),
            );
            segments = lines.map((line) => {
                const cols = line.split(",");
                return {
                    source: cols[longestColumn - 1]?.trim() || line,
                    target: "",
                };
            });
        } else if (fileExt === "po") {
            const msgidMatches = content.match(/msgid\s+"([^"]*)"/g) || [];
            segments = msgidMatches.map((match) => ({
                source: match.replace(/msgid\s+"([^"]*)"/, "$1"),
                target: "",
            }));
        } else if (fileExt === "xliff" || fileExt === "xlf") {
            try {
                const xliffResult = parseXliff(content);
                if (xliffResult.sourceLanguage)
                    setSourceLanguage(xliffResult.sourceLanguage);
                if (xliffResult.targetLanguage)
                    setTargetLanguage(xliffResult.targetLanguage);
                const wordCount = xliffResult.segments.reduce(
                    (sum, seg) =>
                        sum + seg.source.split(" ").filter(Boolean).length,
                    0,
                );
                return {
                    segments: xliffResult.segments.map((seg) => ({
                        source: seg.source,
                        target: seg.target || "",
                    })),
                    wordCount,
                };
            } catch (err) {
                console.warn("XLIFF parse failed:", err);
                const sourceMatches =
                    content.match(/<source>(.*?)<\/source>/gs) || [];
                segments = sourceMatches.map((match: string) => ({
                    source: match
                        .replace(/<source>(.*?)<\/source>/s, "$1")
                        .trim(),
                    target: "",
                }));
            }
        } else {
            let parts = content.split(/\n\s*\n/).filter((p) => p.trim());
            if (parts.length === 1)
                parts = content.split(/[.!?]+/).filter((s) => s.trim());
            if (parts.length < 3)
                parts = content.split("\n").filter((l) => l.trim());
            segments = parts.map((text) => ({
                source: text.trim(),
                target: "",
            }));
        }

        const wordCount = segments.reduce(
            (sum, seg) =>
                sum + (seg.source.split(/\s+/).filter((w) => w).length || 0),
            0,
        );
        return { segments, wordCount };
    };

    const handleFileSelect = async (files: File[]) => {
        setSelectedFiles(files);
        setError(null);
        setPdfComplexityWarning(null);
        setPdfWarningDismissed(false);
        // Check for track changes in any DOCX files
        for (const file of files) {
            if (file.name.toLowerCase().endsWith(".docx")) {
                const hasChanges = await hasTrackChanges(file);
                if (hasChanges) {
                    setError(
                        `"${file.name}" contains unaccepted track changes. Please accept or reject all changes in your word processor before uploading.`,
                    );
                    setSelectedFiles([]);
                    return;
                }
            }
        }
        // Clear parsed result when files change since we can't pre-parse multiple files
        if (files.length === 0) {
            setParsedResult(null);
            setConfirmedTerms([]);
            setTermsAdded(false);
            setSuggestedTerms([]);
            setExtractError(null);
        }
        // Pre-flight complexity check for PDFs — warn, don't block
        const pdfFiles = files.filter((f) =>
            f.name.toLowerCase().endsWith(".pdf"),
        );
        for (const pdfFile of pdfFiles) {
            const result = await checkPdfComplexity(pdfFile);
            if (result?.complex) {
                setPdfComplexityWarning(
                    `"${pdfFile.name}" appears to contain complex formatting or images. Segmentation quality may be reduced — consider requesting a DOCX source if available.`,
                );
                break;
            }
        }
    };

    const handleExtract = async () => {
        if (!parsedResult) return;
        setExtracting(true);
        setExtractError(null);
        try {
            const terms = await extractTerminology(
                parsedResult.segments,
                sourceLanguage,
                targetLanguage,
            );
            setSuggestedTerms(terms);
            setShowTermModal(true);
        } catch (err: any) {
            setExtractError(
                err.message || "AI extraction failed — please try again",
            );
        } finally {
            setExtracting(false);
        }
    };

    const handleToggleTerm = (index: number) => {
        setSuggestedTerms((prev) =>
            prev.map((t, i) =>
                i === index ? { ...t, approved: !t.approved } : t,
            ),
        );
    };

    const handleConfirmTerms = () => {
        setConfirmedTerms(suggestedTerms.filter((t) => t.approved));
        setTermsAdded(true);
        setShowTermModal(false);
    };

    const handleCreateProject = async () => {
        try {
            setUploading(true);
            setError(null);
            setDebugInfo("Starting project creation...");
            if (!user) throw new Error("User not authenticated");
            if (selectedFiles.length === 0)
                throw new Error("Please select at least one file");
            if (!projectName.trim())
                throw new Error("Please enter a project name");

            console.group("Creating Project");

            // Process all files and collect segments
            const allSegments: {
                source: string;
                target: string;
                paragraphIndex?: number;
                sourceFile: string;
                page_number?: number;
                bbox?: { x: number; y: number; width: number; height: number };
                font_meta?: { size: number; name: string };
            }[] = [];
            let totalWordCount = 0;
            const uploadedFileUrls: string[] = [];
            const fileUrls: Record<string, string> = {};
            let fileUrl: string | null = null;

            // Process each file
            for (const file of selectedFiles) {
                setDebugInfo(`Processing ${file.name}...`);

                // Parse file content
                const parsedData = await parseFileContent(file);

                // Add source file name to each segment
                const segmentsWithSourceFile = parsedData.segments.map(
                    (segment) => ({
                        ...segment,
                        sourceFile: file.name,
                    }),
                );

                allSegments.push(...segmentsWithSourceFile);
                totalWordCount += parsedData.wordCount;

                // Upload file to storage
                const filePath = `projects/${user.id}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;
                const { data: uploadData, error: uploadError } =
                    await supabase.storage
                        .from("project-files")
                        .upload(filePath, file, {
                            cacheControl: "3600",
                            upsert: false,
                        });
                if (uploadError) {
                    console.warn(
                        `Storage upload failed for ${file.name}:`,
                        uploadError.message,
                    );
                } else {
                    const { data: urlData } = supabase.storage
                        .from("project-files")
                        .getPublicUrl(filePath);
                    fileUrl = urlData.publicUrl;
                    // Track storage usage
                    try {
                        await incrementStorageUsage(user.id, file.size);
                    } catch (e) {
                        console.warn("Storage tracking failed:", e);
                    }
                    uploadedFileUrls.push(urlData.publicUrl);
                    fileUrls[file.name] = urlData.publicUrl;
                }
            }

            if (allSegments.length === 0)
                throw new Error("No content found in any file.");

            // Create project in database
            setDebugInfo("Creating project record...");
            const { data: projectData, error: projectError } = await supabase
                .from("cat_projects")
                .insert({
                    name: projectName,
                    source_language: sourceLanguage,
                    target_language: targetLanguage,
                    owner_id: user.id,
                    total_segments: allSegments.length,
                    completed_segments: 0,
                    word_count: totalWordCount,
                    status: "active",
                    file_url:
                        uploadedFileUrls.length > 0
                            ? uploadedFileUrls[0]
                            : null,
                    file_urls: fileUrls,
                    file_count: selectedFiles.length,
                    orthographic_standard: orthographicStandard,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                })
                .select()
                .single();
            if (projectError)
                throw new Error(
                    `Failed to create project: ${projectError.message}`,
                );

            setDebugInfo("Creating segments...");
            const { error: segmentsError } = await supabase
                .from("cat_segments")
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .insert(
                    allSegments.map((seg, index) => ({
                        project_id: projectData.id,
                        segment_order: index + 1,
                        source_text: seg.source,
                        xml_paragraph_index: seg.paragraphIndex ?? null,
                        target_text: seg.target || "",
                        source_file: seg.sourceFile,
                        page_number: seg.page_number ?? null,
                        bbox: seg.bbox ?? null,
                        font_meta: seg.font_meta ?? null,
                        status: "new",
                        locked: false,
                        notes: "",
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                    })) as any,
                );
            if (segmentsError)
                throw new Error(
                    `Failed to create segments: ${segmentsError.message}`,
                );

            if (confirmedTerms.length > 0) {
                setDebugInfo(
                    `Adding ${confirmedTerms.length} glossary terms...`,
                );
                const {
                    data: { user: authUser },
                } = await supabase.auth.getUser();
                const { error: glossaryError } = await supabase
                    .from("terminology")
                    .insert(
                        confirmedTerms.map((t) => ({
                            term_source: t.term_source,
                            term_target: t.term_target,
                            source_language: sourceLanguage,
                            target_language: targetLanguage,
                            definition: t.definition || null,
                            domain: t.domain || null,
                            notes: null,
                            created_by: authUser?.id || user.id,
                        })),
                    );
                if (glossaryError)
                    console.warn(
                        "Glossary insert warning:",
                        glossaryError.message,
                    );
                else
                    console.log("Glossary terms added:", confirmedTerms.length);
            }

            console.groupEnd();
            setDebugInfo("Project created successfully!");
            setTimeout(
                () => navigate(`/projects/${projectData.id}/editor`),
                500,
            );
        } catch (err: any) {
            console.error("Project creation failed:", err);
            console.groupEnd();
            setError(err.message || "Failed to create project");
            setDebugInfo("Error: " + err.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            {showTermModal && suggestedTerms.length > 0 && (
                <TermModal
                    terms={suggestedTerms}
                    onToggle={handleToggleTerm}
                    onConfirm={handleConfirmTerms}
                    onClose={() => setShowTermModal(false)}
                />
            )}
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-3xl mx-auto px-4">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        {/* Step indicator */}
                        <div className="mb-8">
                            <h1 className="text-2xl font-bold text-gray-900 mb-6">
                                Create New Translation Project
                            </h1>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    {[1, 2, 3].map((step) => (
                                        <React.Fragment key={step}>
                                            <div
                                                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                                                    currentStep === step
                                                        ? "bg-gold-600 text-white"
                                                        : currentStep > step
                                                          ? "bg-green-600 text-white"
                                                          : "bg-gray-200 text-gray-600"
                                                }`}
                                            >
                                                {step}
                                            </div>
                                            {step < 3 && (
                                                <div
                                                    className={`w-16 h-1 ${
                                                        currentStep > step
                                                            ? "bg-green-600"
                                                            : "bg-gray-200"
                                                    }`}
                                                />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div className="text-sm text-gray-600">
                                    Step {currentStep} of 3
                                </div>
                            </div>
                            <div className="flex justify-between mt-2 text-sm text-gray-600">
                                <div
                                    className={
                                        currentStep === 1
                                            ? "font-medium text-gold-700"
                                            : ""
                                    }
                                >
                                    Project Details
                                </div>
                                <div
                                    className={
                                        currentStep === 2
                                            ? "font-medium text-gold-700"
                                            : ""
                                    }
                                >
                                    Upload Document
                                </div>
                                <div
                                    className={
                                        currentStep === 3
                                            ? "font-medium text-gold-700"
                                            : ""
                                    }
                                >
                                    Options & Confirm
                                </div>
                            </div>
                        </div>

                        {/* Step 1: Project Details */}
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Project Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={projectName}
                                        onChange={(e) =>
                                            setProjectName(e.target.value)
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                                        placeholder="e.g., African Union Documentation Q4 2024"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Source Language *
                                        </label>
                                        <select
                                            value={sourceLanguage}
                                            onChange={(e) =>
                                                setSourceLanguage(
                                                    e.target.value,
                                                )
                                            }
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                                        >
                                            {LANGUAGE_OPTIONS.map((l) => (
                                                <option
                                                    key={l.value}
                                                    value={l.value}
                                                >
                                                    {l.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Target Language *
                                        </label>
                                        <select
                                            value={targetLanguage}
                                            onChange={(e) =>
                                                setTargetLanguage(
                                                    e.target.value,
                                                )
                                            }
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent"
                                        >
                                            {LANGUAGE_OPTIONS.map((l) => (
                                                <option
                                                    key={l.value}
                                                    value={l.value}
                                                >
                                                    {l.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {/* Orthographic Standard */}
                                {PT_PREAO_TARGETS.has(targetLanguage) && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Orthographic Standard
                                            <span className="ml-2 text-xs text-gray-500">
                                                PT-PT only
                                            </span>
                                        </label>
                                        <div className="flex gap-3">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setOrthographicStandard(
                                                        "post_ao",
                                                    )
                                                }
                                                className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                                                    orthographicStandard ===
                                                    "post_ao"
                                                        ? "bg-gold-600 border-gold-600 text-white"
                                                        : "bg-white border-gray-300 text-gray-700 hover:border-gold-400"
                                                }`}
                                            >
                                                <div className="font-semibold">
                                                    Post-Reform
                                                </div>
                                                <div className="text-xs opacity-80 mt-0.5">
                                                    1990 Spelling Agreement
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setOrthographicStandard(
                                                        "pre_ao",
                                                    )
                                                }
                                                className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                                                    orthographicStandard ===
                                                    "pre_ao"
                                                        ? "bg-gold-600 border-gold-600 text-white"
                                                        : "bg-white border-gray-300 text-gray-700 hover:border-gold-400"
                                                }`}
                                            >
                                                <div className="font-semibold">
                                                    Pre-AO
                                                </div>
                                                <div className="text-xs opacity-80 mt-0.5">
                                                    Angola · Mozambique · Legacy
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 2: Upload Document */}
                        {currentStep === 2 && (
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Source File *
                                    </label>
                                    <FileUpload
                                        onFileSelect={handleFileSelect}
                                        selectedFiles={selectedFiles}
                                        error={error || undefined}
                                    />
                                    <div className="text-center mt-2">
                                        <span className="text-xs text-gray-400">
                                            or{" "}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const sample =
                                                    createSampleFile();
                                                setSelectedFiles([sample]);
                                            }}
                                            className="text-sm text-gold-600 hover:text-gold-700 font-semibold underline underline-offset-2 transition-colors"
                                        >
                                            Try with a sample document
                                        </button>
                                    </div>
                                </div>

                                {selectedFiles.length > 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <div className="flex items-center space-x-3">
                                            <CheckCircle className="h-5 w-5 text-green-600" />
                                            <div>
                                                <p className="text-sm font-medium text-green-900">
                                                    {selectedFiles.length} file
                                                    {selectedFiles.length !== 1
                                                        ? "s"
                                                        : ""}{" "}
                                                    selected
                                                </p>
                                                <p className="text-xs text-green-700">
                                                    Total size:{" "}
                                                    {(
                                                        selectedFiles.reduce(
                                                            (sum, file) =>
                                                                sum + file.size,
                                                            0,
                                                        ) /
                                                        1024 /
                                                        1024
                                                    ).toFixed(2)}{" "}
                                                    MB
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {pdfComplexityWarning &&
                                    !pdfWarningDismissed && (
                                        <div className="bg-gold-50 border border-gold-200 rounded-lg p-4 flex items-start gap-3">
                                            <AlertCircle className="h-5 w-5 text-gold-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-slate-900">
                                                    Complex PDF detected
                                                </p>
                                                <p className="text-xs text-slate-700 mt-0.5">
                                                    {pdfComplexityWarning}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setPdfWarningDismissed(
                                                        true,
                                                    )
                                                }
                                                className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                                                aria-label="Dismiss warning"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* Step 3: Options and Confirm */}
                        {currentStep === 3 && (
                            <div className="space-y-6">
                                {/* MT Provider Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Machine Translation Provider
                                    </label>
                                    <div className="grid grid-cols-3 gap-3">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setMtProvider("deepl")
                                            }
                                            className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                                                mtProvider === "deepl"
                                                    ? "bg-gold-600 border-gold-600 text-white"
                                                    : "bg-white border-gray-300 text-gray-700 hover:border-gold-400"
                                            }`}
                                        >
                                            DeepL
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setMtProvider("google")
                                            }
                                            className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                                                mtProvider === "google"
                                                    ? "bg-gold-600 border-gold-600 text-white"
                                                    : "bg-white border-gray-300 text-gray-700 hover:border-gold-400"
                                            }`}
                                        >
                                            Google Translate
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setMtProvider("mymemory")
                                            }
                                            className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                                                mtProvider === "mymemory"
                                                    ? "bg-gold-600 border-gold-600 text-white"
                                                    : "bg-white border-gray-300 text-gray-700 hover:border-gold-400"
                                            }`}
                                        >
                                            MyMemory
                                        </button>
                                    </div>
                                </div>

                                {/* AI Glossary Extraction Toggle */}
                                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">
                                            AI Glossary Extraction
                                        </p>
                                        <p className="text-xs text-gray-600 mt-0.5">
                                            Automatically extract key terms from
                                            your document
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setAiGlossaryExtraction(
                                                !aiGlossaryExtraction,
                                            )
                                        }
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                            aiGlossaryExtraction
                                                ? "bg-gold-600"
                                                : "bg-gray-300"
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                aiGlossaryExtraction
                                                    ? "translate-x-6"
                                                    : "translate-x-1"
                                            }`}
                                        />
                                    </button>
                                </div>

                                {/* Summary Card */}
                                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                                        Project Summary
                                    </h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">
                                                Project Name:
                                            </span>
                                            <span className="font-medium">
                                                {projectName || "Not set"}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">
                                                Language Pair:
                                            </span>
                                            <span className="font-medium">
                                                {sourceLanguage.toUpperCase()} →{" "}
                                                {targetLanguage.toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">
                                                File:
                                            </span>
                                            <span className="font-medium">
                                                {selectedFiles.length > 0
                                                    ? `${selectedFiles.length} file${selectedFiles.length !== 1 ? "s" : ""}`
                                                    : "No files selected"}
                                            </span>
                                        </div>
                                        {PT_PREAO_TARGETS.has(
                                            targetLanguage,
                                        ) && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">
                                                    Orthographic Standard:
                                                </span>
                                                <span className="font-medium">
                                                    {orthographicStandard ===
                                                    "post_ao"
                                                        ? "Post-Reform"
                                                        : "Pre-Reform"}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">
                                                MT Provider:
                                            </span>
                                            <span className="font-medium capitalize">
                                                {mtProvider}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">
                                                AI Glossary Extraction:
                                            </span>
                                            <span className="font-medium">
                                                {aiGlossaryExtraction
                                                    ? "Enabled"
                                                    : "Disabled"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* AI Terminology Extraction Section */}
                                {aiGlossaryExtraction &&
                                    parsedResult &&
                                    parsedResult.segments.length > 0 && (
                                        <div className="rounded-lg border border-gold-200 bg-gold-50 p-4">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-start space-x-3">
                                                    <Sparkles className="h-5 w-5 text-gold-600 flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <p className="text-sm font-medium text-gold-900">
                                                            {termsAdded
                                                                ? `✓ ${confirmedTerms.length} term${confirmedTerms.length !== 1 ? "s" : ""} ready for glossary`
                                                                : "AI Terminology Extraction"}
                                                        </p>
                                                        <p className="text-xs text-gold-700 mt-0.5">
                                                            {termsAdded
                                                                ? "These will be saved to your glossary when the project is created."
                                                                : "Let TranslyxAI scan your document and suggest key terms for your glossary."}
                                                        </p>
                                                        {extractError && (
                                                            <p className="text-xs text-red-600 mt-1">
                                                                {extractError}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    {termsAdded && (
                                                        <button
                                                            onClick={() =>
                                                                setShowTermModal(
                                                                    true,
                                                                )
                                                            }
                                                            className="text-xs text-gold-700 hover:text-gold-900 underline underline-offset-2 transition-colors"
                                                        >
                                                            Review
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={handleExtract}
                                                        disabled={extracting}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold-600 hover:bg-gold-700 disabled:bg-gold-400 text-white text-sm font-medium rounded-lg transition-colors"
                                                    >
                                                        {extracting ? (
                                                            <>
                                                                <Loader className="h-3.5 w-3.5 animate-spin" />{" "}
                                                                Extracting...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Sparkles className="h-3.5 w-3.5" />{" "}
                                                                {termsAdded
                                                                    ? "Re-extract"
                                                                    : "Extract Terminology"}
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex justify-between space-x-3 pt-8 border-t border-gray-200 mt-8">
                            <div>
                                {currentStep > 1 && (
                                    <Button
                                        variant="secondary"
                                        onClick={() =>
                                            setCurrentStep(currentStep - 1)
                                        }
                                        disabled={uploading}
                                    >
                                        Back
                                    </Button>
                                )}
                            </div>
                            <div className="flex space-x-3">
                                {currentStep < 3 ? (
                                    <>
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                navigate("/projects")
                                            }
                                            disabled={uploading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() =>
                                                setCurrentStep(currentStep + 1)
                                            }
                                            disabled={
                                                (currentStep === 1 &&
                                                    !canGoToStep2) ||
                                                (currentStep === 2 &&
                                                    !canGoToStep3) ||
                                                uploading
                                            }
                                        >
                                            Next
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                navigate("/projects")
                                            }
                                            disabled={uploading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleCreateProject}
                                            disabled={
                                                !canCreateProject || uploading
                                            }
                                            leftIcon={
                                                uploading ? (
                                                    <Loader className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <FileText className="h-4 w-4" />
                                                )
                                            }
                                        >
                                            {uploading
                                                ? "Creating Project..."
                                                : "Create Project"}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                        {debugInfo && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                                <p className="text-sm text-blue-700 flex items-center space-x-2">
                                    {uploading && (
                                        <Loader className="h-4 w-4 animate-spin" />
                                    )}
                                    <span>{debugInfo}</span>
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default NewProjectPage;
